import { useEffect, useState } from "react";
import { Banknote, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, gbp } from "../lib/api";
import { Switch } from "./ui/switch";

export function AutoPayoutsPanel() {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/admin/payouts/overview").then((r) => setD(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  if (!d) return null;
  const toggle = async (enabled) => {
    setBusy(true);
    try { const { data } = await api.put("/admin/payouts/settings", { enabled }); toast.success(enabled ? `Automatic payouts on${data.sent ? ` · ${data.sent} sent` : ""}` : "Automatic payouts off"); load(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const run = async () => {
    setBusy(true);
    try { const { data } = await api.post("/admin/payouts/run"); toast.success(`${data.sent} payout${data.sent === 1 ? "" : "s"} sent`); load(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="card p-5 space-y-3" data-testid="auto-payouts-panel">
      <div className="flex items-start gap-3 justify-between flex-wrap">
        <div className="flex gap-3"><Banknote className="text-[#FF8166] shrink-0" />
          <div><h2 className="font-bold">Automatic payouts</h2>
            <p className="text-sm muted">When on, a professional's earnings are sent to their bank as soon as a job is completed, if they've set up their payout account. When off, you mark payouts as paid yourself.</p></div></div>
        <label className="flex items-center gap-2 text-sm font-semibold"><span data-testid="auto-payouts-state">{d.enabled ? "On" : "Off"}</span>
          <Switch checked={d.enabled} disabled={busy} onCheckedChange={toggle} data-testid="auto-payouts-toggle" /></label>
      </div>
      {!d.connect_ready && <p className="text-xs bg-amber-50 text-amber-800 rounded-lg p-3 flex gap-2" data-testid="connect-not-ready"><AlertTriangle size={14} className="shrink-0 mt-0.5" />Stripe Connect isn't switched on for your Stripe account yet. In your Stripe dashboard open Connect, click Get started and choose "Platform or marketplace". Then you can turn this on.</p>}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
        <div className="bg-slate-50 rounded-lg p-3"><span className="muted text-xs block">Pending payouts</span><b data-testid="payouts-pending-count">{d.pending_count}</b> · {gbp(d.pending_total)}</div>
        <div className="bg-slate-50 rounded-lg p-3"><span className="muted text-xs block">Ready to send</span><b>{d.pending_ready}</b></div>
        <div className="bg-slate-50 rounded-lg p-3"><span className="muted text-xs block">Pros with payout account</span><b>{d.connected_pros}</b></div>
        <div className="bg-slate-50 rounded-lg p-3"><span className="muted text-xs block">Pros ready for payouts</span><b>{d.ready_pros}</b></div>
      </div>
      {d.failed.length > 0 && <div className="text-xs text-red-700 space-y-1" data-testid="payouts-failed">{d.failed.map((f) => <p key={f.id}>{gbp(f.net)} not sent: {f.last_error}</p>)}</div>}
      {d.enabled && d.pending_ready > 0 && <button onClick={run} disabled={busy} className="btn-ghost btn-sm" data-testid="payouts-run-btn">Send ready payouts now</button>}
    </div>
  );
}

export function PayoutAccountCard() {
  const [a, setA] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/provider/payouts/account").then((r) => setA(r.data)).catch(() => {}); }, []);
  if (!a) return null;
  const go = async (path, body) => {
    setBusy(true);
    try { const { data } = await api.post(path, body); window.location.href = data.url; }
    catch (e) { toast.error(errMsg(e)); setBusy(false); }
  };
  const ready = a.payouts_enabled;
  return (
    <div className="card p-5 flex flex-col sm:flex-row gap-4 sm:items-center justify-between" data-testid="payout-account-card">
      <div className="flex gap-3"><Banknote className="text-[#FF8166] shrink-0" />
        <div><h2 className="font-bold">Payout account</h2>
          <p className="text-sm muted" data-testid="payout-account-status">{ready ? "Ready: your earnings can be paid straight to your bank." : a.connected ? "Almost there: finish setting up your payout account to get paid automatically." : "Set up your payout account so your earnings can be paid straight to your bank."}</p>
          {!a.auto_payouts && <p className="text-xs muted mt-1">Automatic payouts aren't switched on yet. Until then MyHomelyHub pays you manually.</p>}</div></div>
      {ready
        ? <button onClick={() => go("/provider/payouts/dashboard", {})} disabled={busy} className="btn-ghost btn-sm" data-testid="payout-dashboard-btn">View payouts in Stripe</button>
        : <button onClick={() => go("/provider/payouts/onboard", { origin_url: window.location.origin })} disabled={busy} className="btn-coral btn-sm" data-testid="payout-setup-btn">{a.connected ? "Finish setup" : "Set up payouts"}</button>}
    </div>
  );
}
